/* eslint-disable no-underscore-dangle */
import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import makeComponentTrashable from 'trashable-react';
import {
  fetchDebtors,
  fetchDebtorCurrentLedger,
  fetchDebtorAveragePayments,
} from 'Shared/util/web-api';
import { formatShortDate, formatAmount } from 'Shared/util/formatters';
import DataTable, { ColumnTypes } from 'Shared/components/tables/data-table';
import Page from 'Shared/components/page/page';
import { getPieChartFilterValue } from 'Shared/components/charts/pie-chart-utils';
import { translateColumns } from 'Shared/util/translations';
import useTranslationContext from 'Contexts/translation-context';
import { useTranslation } from 'Shared/util/custom-hooks';
import { getBarChartData } from './debtor-utils';
import DebtorPieChart from './debtor-pie-chart';
import DebtorBarChart from './debtor-bar-chart';
import styles from './debtors-page.css';


const columns = [
  {
    title: 'customer',
    key: 'name',
  },
  {
    title: 'quantity',
    key: 'numberOfRemaining',
    type: ColumnTypes.Amount,
  },
  {
    title: 'balance',
    key: 'remainingAmount',
    type: ColumnTypes.Amount,
    formatter: (d) => formatAmount(d),
    footer: {
      sum: true,
    },
  },
  {
    title: 'paid',
    key: 'numberOfPaid',
    type: ColumnTypes.Amount,
  },
  {
    title: 'payedAmount',
    key: 'payedAmount',
    type: ColumnTypes.Amount,
    formatter: (d) => formatAmount(d),
    footer: {
      sum: true,
    },
  },
  {
    title: 'paymentTime',
    key: 'averagePaymentDays',
    type: ColumnTypes.Amount,
    formatter: (d) => (d !== null ? formatAmount(d, 1) : ''),
  },
];

const getRowLink = (row) => `/customers/${row.id}`;


const DebtorsPage = ({
  registerPromise,
}) => {
  const [debtors, setDebtors] = useState([]);
  const [currentLedger, setCurrentLedger] = useState([]);
  const [averagePayments, setAveragePayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const history = useHistory();
  const { translationData } = useTranslationContext();
  const t = useTranslation(x => x.debtorsPage);
  const tg = useTranslation(x => x.global);

  useEffect(() => {
    Promise.all([
      registerPromise(fetchDebtors()),
      registerPromise(fetchDebtorCurrentLedger()),
      registerPromise(fetchDebtorAveragePayments()),
    ])
      .then(([debtorsResult, currentLedgerResult, averagePaymentsResult]) => {
        setDebtors(debtorsResult);
        setCurrentLedger(currentLedgerResult);
        setAveragePayments(averagePaymentsResult);
        setLoading(false);
      });
  }, []);

  const handlePieChartClick = (label, index) => {
    history.push({
      pathname: '/ledger',
      state: { pieChartLabelWithIndex: getPieChartFilterValue(null, index, label) },
    });
  };

  const barChartData = formatShortDate(getBarChartData(averagePayments), translationData.languageCode);
  return (
    <Page
      title={t.myCustomers}
      loading={loading}
    >
      <div className={styles.chartContainer}>
        <DebtorPieChart
          headerText={t.currentLedger}
          invoices={currentLedger}
          onClick={(element, chart) => handlePieChartClick(chart.props.data.labels[element._index], element._index)}
          legend={{
            onClick: (label, index) => handlePieChartClick(label, index),
          }}
        />
        <DebtorBarChart
          headerText={t.averagePaymentPeriodAfterDueDate}
          label={t.paymentTime}
          data={barChartData}
        />
      </div>
      <DataTable
        columns={translateColumns(columns, tg)}
        rows={debtors}
        getRowLink={getRowLink}
      />
    </Page>
  );
};

DebtorsPage.propTypes = {
  registerPromise: PropTypes.func.isRequired,
};

export default makeComponentTrashable(DebtorsPage);
